import type { ITelemetryItem } from '@microsoft/applicationinsights-web'
import { defineNuxtPlugin } from '#app'
import { getCookieValue } from '~/utils/auth'
import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'

const firstSegment = (raw: string | undefined): string | null => {
  if (!raw) { return null }
  try {
    return decodeURIComponent(raw).split('^')[0] || null
  } catch {
    return null
  }
}

let cachedRecordingUrl: string | null = null

export const getClarityRecordingUrl = (projectId: string | undefined): string | null => {
  if (!projectId) { return null }
  if (cachedRecordingUrl) { return cachedRecordingUrl }
  let userId: string | null
  let sessionId: string | null
  try {
    userId = firstSegment(getCookieValue('_clck'))
    sessionId = firstSegment(getCookieValue('_clsk'))
  } catch {
    return null
  }
  if (!userId || !sessionId) { return null }
  cachedRecordingUrl = `https://clarity.microsoft.com/player/${projectId}/${userId}/${sessionId}`
  return cachedRecordingUrl
}

export const resetClarityRecordingUrlCache = (): void => {
  cachedRecordingUrl = null
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useTypedRuntimeConfig()
  const clarityProjectId = config.public.CLARITY_PROJECT_ID
  const appInsights = nuxtApp.$appInsights

  // Stamp a direct Clarity recording URL on every App Insights event by reading
  // the _clck (user ID) and _clsk (session ID) cookies synchronously. Both are
  // set by Clarity once a session is established, so early events before Clarity
  // initializes will not have it.
  appInsights?.addTelemetryInitializer((envelope: ITelemetryItem) => {
    const recordingUrl = getClarityRecordingUrl(clarityProjectId)
    if (recordingUrl && envelope.data) {
      envelope.data.clarityRecordingUrl = recordingUrl
    }
  })
})
